import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, MessageSquare } from "lucide-react";
import Logo from "./Logo";
import { CONTACT } from "../data/contact";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/90 backdrop-blur-xl border-b border-gray-900" : "bg-transparent"
      }`}
    >
      <div className="px-5 sm:px-8 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="flex items-center"
            aria-label="SPINLAB Home"
          >
            <Logo size="sm" testId="nav-logo" />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[11px] font-display font-light uppercase tracking-[0.25em] transition-colors ${
                    isActive ? "text-white" : "text-gray-500 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={CONTACT.smsHref}
              data-testid="nav-cta-call"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-gray-800 text-gray-200 hover:border-white hover:text-white transition-all"
              aria-label={`Text ${CONTACT.phone}`}
            >
              <MessageSquare strokeWidth={1} size={16} />
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta-quote"
              className="hidden lg:inline-flex items-center px-6 py-3 border border-gray-700 text-[11px] font-display font-light uppercase tracking-[0.25em] text-gray-200 hover:border-white hover:text-white transition-all"
            >
              Request Quote
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
              className="lg:hidden text-gray-300 hover:text-white p-1"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X strokeWidth={1} size={24} /> : <Menu strokeWidth={1} size={24} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden border-t border-gray-900 bg-black/95 backdrop-blur-xl"
        >
          <div className="px-6 py-8 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-display font-light uppercase tracking-[0.3em] ${
                    isActive ? "text-white" : "text-gray-500"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="hairline my-3" />
            <a
              href={CONTACT.smsHref}
              data-testid="mobile-nav-phone"
              className="inline-flex items-center gap-3 text-sm font-display font-light tracking-[0.2em] text-gray-200"
            >
              <MessageSquare strokeWidth={1} size={16} /> Text {CONTACT.phone}
            </a>
            <Link
              to="/contact"
              data-testid="mobile-nav-cta-quote"
              className="mt-2 inline-flex items-center justify-center px-6 py-4 border border-gray-200 text-[11px] font-display font-light uppercase tracking-[0.3em] text-white"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
